import url from 'url';
import { addParameterToUrl } from '@/appsync/service/GraphqlUtils';
import { i18n } from '@/plugins/i18n/i18n';
import {
  Product,
  TenantLandscape,
  ProductMenu,
  ProductAdditionalQueryMap,
} from '@/types/type';
import { LANDSCAPE_PRODUCTION, ApplicationDomain } from '@/utils/consts';

enum RootDomain {
  prod = 'company.works-hi.com',
  dev = 'company.lab.works-hi.com',
}

type Consumer<T> = () => T;

type RootMenuUrlCreator<T> = {
  [key in Product]: Consumer<T>;
};

const getHost = (tenantLandscape: TenantLandscape) => {
  return tenantLandscape.landscape === LANDSCAPE_PRODUCTION
    ? tenantLandscape.tenant
    : `${tenantLandscape.tenant}-${tenantLandscape.landscape}`;
};

export const createProductRootMenuUrl = (
  portalHostname: string,
  tenantLandscape: TenantLandscape,
  product: Product
): string => {
  const host = getHost(tenantLandscape);
  const rootDomain =
    portalHostname.includes(ApplicationDomain.portalCommercial) ||
    portalHostname.includes(ApplicationDomain.fbsCommercial)
      ? RootDomain.prod
      : RootDomain.dev;
  const getHostName: RootMenuUrlCreator<string> = {
    CSR: () => `${host}-cws.${rootDomain}`,
    CWS: () => `${host}-cws.${rootDomain}`,
    CJK: () => `${host}-cjk.${rootDomain}`,
    CTM: () => `${host}-ctm.${rootDomain}`,
  };
  const hostname = getHostName[product]();
  const getPathName: RootMenuUrlCreator<string> = {
    CSR: () => '/self-workflow/cws/cws',
    CWS: () => '/self-workflow/cws/cws',
    CJK: () => '/CPNYCJK/cjkweb',
    CTM: () => '/ctm/ctm/portal/main/',
  };
  const pathname = getPathName[product]();
  const getSearch: RootMenuUrlCreator<null | string> = {
    CSR: () => '@SUB=root.cws.shuro&@SN=root.cws.shuro',
    CWS: () => null,
    CJK: () => null,
    CTM: () => null,
  };
  const search = getSearch[product]();
  return url.format({
    protocol: 'https',
    hostname,
    pathname,
    search,
  });
};

export const createProductRootMenu = (
  available: boolean,
  portalHostname: string,
  tenantLandscape: TenantLandscape,
  product: Product,
  productAdditionalQueryMap?: ProductAdditionalQueryMap
): ProductMenu => {
  const title =
    tenantLandscape.tenant === 'takeda'
      ? i18n.tc(`productMainMenuForTakeda.${product.toString()}`)
      : i18n.tc(`productMainMenu.${product.toString()}`);
  const baseUrl = createProductRootMenuUrl(
    portalHostname,
    tenantLandscape,
    product
  );
  const url = addParameterToUrl(baseUrl, product, productAdditionalQueryMap);
  return {
    id: product.toString(),
    title,
    url,
    icon: 'mdi-briefcase',
    menuType: 'menu',
    product,
    description: '',
    available,
    isFavorite: false,
    canUseFromExtra: true,
  };
};
